import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { type IconProps } from "@phosphor-icons/react";

import { cn } from "../../lib/utils";
import { Icon, type PhosphorIcon } from "./icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

const buttonVariants = cva(
  "ui-flex ui-justify-center ui-items-center ui-transition-all ui-duration-200",
  {
    variants: {
      variant: {
        primary:
          "ui-z-1 ui-relative bg-icon-button-bright shadow-icon-button-bright \
          hover:ui-bg-black/10 ui-text-white \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-transparent \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed disabled:hover:ui-bg-transparent \
          active:ui-bg-black/20",
        outline:
          "ui-z-1 ui-relative ui-bg-white ui-text-zinc-500 \
          before:ui-absolute before:ui-border-1 before:ui-border-[var(--before-border-color,rgba(228,228,231))] before:ui-w-full before:ui-h-full before:ui-z-[0] \
          hover:before:ui-border-[var(--before-border-color-hover,rgba(212,212,216))] hover:ui-bg-black/4 \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-white \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed disabled:hover:ui-bg-white disabled:hover:before:ui-border-[var(--before-border-color,rgba(228,228,231))] \
          active:ui-bg-black/6 active:after:ui-bg-white active:before:ui-border-[var(--before-border-color-active,rgba(161,161,170))] ui-bg-white",
        ghost:
          "ui-z-1 ui-relative ui-bg-transparent ui-text-zinc-500 \
          hover:ui-bg-black/6 ui-text-white \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-transparent \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed disabled:hover:ui-bg-transparent \
          active:ui-bg-black/12 active:after:ui-bg-white active:before:ui-border-zinc-400 ui-bg-transparent",
        error:
          "ui-z-1 ui-relative ui-bg-red-700 ui-text-white \
          hover:bg-error-gradient ui-text-white  \
          focus-visible:ui-outline-none focus-visible:ui-ring-1 focus-visible:ui-ring-indigo-500/50 focus-visible:ui-ring-offset-1 focus-visible:ui-ring-offset-transparent \
          disabled:ui-opacity-40 disabled:ui-cursor-not-allowed disabled:hover:ui-bg-red-700 \
          active:bg-error-active ui-text-white",
      },
      size: {
        xxs: "ui-text-text-xs-semibold ui-rounded-[4px] ui-p-[1px] before:ui-rounded-[4px] focus-visible:after:ui-rounded-[8px] ",
        xs: "ui-text-text-xs-semibold ui-rounded-[6px] ui-p-[2px] before:ui-rounded-[6px] focus-visible:after:ui-rounded-[12px] ",
        sm: "ui-p-[4px] ui-text-text-xs-semibold ui-rounded-lg  before:ui-rounded-lg after:ui-rounded-[10px] focus-visible:after:ui-rounded-xl ",
        md: "ui-p-[6px] ui-text-text-sm-semibold ui-rounded-lg  before:ui-rounded-lg after:ui-rounded-[10px] focus-visible:after:ui-rounded-xl ",
        lg: "ui-p-[10px] ui-text-text-sm-semibold ui-rounded-xl  before:ui-rounded-xl after:ui-rounded-[14px] focus-visible:after:ui-rounded-[14px] ",
        xl: "ui-p-[14px] ui-text-text-sm-semibold ui-rounded-xl  before:ui-rounded-xl after:ui-rounded-[14px] focus-visible:after:ui-rounded-[14px] ",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "sm",
    },
    compoundVariants: [
      {
        variant: "primary",
        size: ["xxs", "xs", "sm", "md", "lg", "xl"],
        className: "shadow-surface-bright ",
      },
    ],
  }
);

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  IconComponent: PhosphorIcon;
  iconProps?: Omit<IconProps, "size">;
  size: NonNullable<VariantProps<typeof buttonVariants>["size"]>;
  /** Optional tooltip configuration; provide to enable tooltip */
  tooltip?: {
    text: React.ReactNode;
    side?: "top" | "right" | "bottom" | "left";
    sideOffset?: number;
    delayDuration?: number;
    className?: string;
  };
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      IconComponent,
      iconProps,
      tooltip,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    const buttonEl = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        <Icon IconComponent={IconComponent} size={size} iconProps={iconProps} />
      </Comp>
    );

    if (tooltip && tooltip.text) {
      const {
        text,
        side = "top",
        sideOffset = 1,
        delayDuration = 0,
        className,
      } = tooltip;
      return (
        <Tooltip delayDuration={delayDuration}>
          <TooltipTrigger asChild>{buttonEl}</TooltipTrigger>
          <TooltipContent
            side={side}
            sideOffset={sideOffset}
            className={className}
          >
            {text}
          </TooltipContent>
        </Tooltip>
      );
    }

    return buttonEl;
  }
);
IconButton.displayName = "IconButton";

export { IconButton, buttonVariants as iconButtonVariants };
